import * as React from "react";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { Box, Button } from "@mui/material";
import { apiService } from "../../../../api/apiService";

interface FilePreviewProps {
  loading: boolean;
  error: string | null;
  imageSrc: string | null;
  name: string; // Add name prop to determine file type
  docPath?: string; // Add docPath prop to pass the document path for download
  fileSize?: number; // Add fileSize prop to determine the size of the file
}

const FilePreview: React.FC<FilePreviewProps> = ({
  loading,
  error,
  imageSrc,
  name,
  docPath,
  fileSize,
}) => {
  const getFileType = (fileName: string): string => {
    const parts = fileName.split(".");
    if (parts.length > 1) {
      return parts.pop()!.toLowerCase();
    }
    return "unknown";
  };

  const fileType = getFileType(name);

  const isImageFile = ["png", "jpg", "jpeg"].includes(fileType);
  const isPdfFile = fileType === "pdf";
  const isTextFile = ["txt", "csv", "log"].includes(fileType);
  const isUnsupportedFile = [
    "pptx",
    "docx",
    "doc",
    "ppt",
    "xlsx",
    "xls",
    "xlsm",
  ].includes(fileType);

  const handleDownload = () => {
    if (!imageSrc) {
      console.error("No file data available to download.");
      return;
    }

    try {
      // Convert Base64 to a Blob
      const byteCharacters = atob(imageSrc);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: `application/${fileType}` });

      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  const getFileSizeInBytes = (base64: string): number => {
    const padding = (base64.match(/=+$/) || [""])[0].length;
    return (base64.length * 3) / 4 - padding;
  };

  const PDF_SIZE_LIMIT = 1 * 1024 * 1024;
//   const PDF_SIZE_LIMIT = 10 * 1024;

  const actualFileSize = imageSrc
    ? getFileSizeInBytes(imageSrc)
    : (fileSize ?? 0);

  const isFileTooLarge = isPdfFile
    ? actualFileSize > PDF_SIZE_LIMIT
    : actualFileSize > 2 * 1024 * 1024;

  console.log("isFileTooLarge", isFileTooLarge);
  console.log("fileSize", fileSize);

  return (
    <DialogContent sx={{ overflowY: "hidden", height: "450px" }}>
        {/* check if this is the right component */}
      {loading ? (
        <div style={{ textAlign: "center" }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading file preview...
          </Typography>
        </div>
      ) : error ? (
        <Typography color="error" variant="body2">
          {error}
        </Typography>
      ) : isFileTooLarge ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2">
            File size is too large to preview. Please download thes file to
            view.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      ) : imageSrc ? (
        <>
          {isPdfFile ? (
            <Box sx={{ position: "relative" }}>
              {actualFileSize <= PDF_SIZE_LIMIT ? (
                <iframe
                  src={`data:application/pdf;base64,${imageSrc}`}
                  width="100%"
                  height="100%"
                  style={{ height: "500px" }}
                  title="PDF Preview"
                />
              ) : (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography variant="body2">
                    PDF file is too large to preview. Please download to view. (
                    {Math.round(actualFileSize / 1024)}KB /{" "}
                    {Math.round(PDF_SIZE_LIMIT / 1024)}KB)
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleDownload}
                    sx={{ mt: 2 }}
                    size="small"
                  >
                    Download PDF
                  </Button>
                </Box>
              )}
            </Box>
          ) : isImageFile ? (
            <Box
              component="img"
              src={`data:image/${fileType};base64,${imageSrc}`}
              alt="Image Preview"
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                height: "500px",
                objectFit: "contain",
              }}
            />
          ) : isTextFile ? (
            <Typography
              component="pre"
              sx={{
                whiteSpace: "pre-wrap",
                overflow: "auto",
                maxHeight: "500px",
              }}
            >
              {atob(imageSrc)}
            </Typography>
          ) : isUnsupportedFile ? (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2">
                {/* Preview not available for {fileType.toUpperCase()} files (PPTX, DOCX, DOC, PPT, XLSX, XLS, XLSM). Please download to view. */}
                Preview not available for this file type. Please download to
                view.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                sx={{ mt: 2 }}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2">
                {/* Preview not available for {fileType.toUpperCase()} files. Please download to view. */}
                Preview not available for this file type. Please download to
                view.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                sx={{ mt: 2 }}
              >
                Download File
              </Button>
            </Box>
          )}
        </>
      ) : isUnsupportedFile ? (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2">
            {/* Preview not available for {fileType.toUpperCase()} files (PPTX, DOCX, DOC, PPT, XLSX, XLS, XLSM). Please download to view. */}
            Preview not available for this file type. Please download to view.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      ) : (
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body2">
            {/* Preview not available for {fileType.toUpperCase()} files. Please download to view. */}
            Preview not available for this file type. Please download to view.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      )}
    </DialogContent>
  );
};

export default FilePreview;
